import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { play } from "../lib/sound";
import { MessageCircle, X, Send, Bot, Store, ClipboardList, LifeBuoy, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Cosa mi consigli?",
  "Vorrei una pizza senza glutine",
  "Come funziona la consegna?",
  "Dov'è il mio ordine?",
];

function sessionId() {
  let s = localStorage.getItem("fuma_assist_session");
  if (!s) { s = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("fuma_assist_session", s); }
  return s;
}

export default function AssistantWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [enabled, setEnabled] = useState(false);
  const [greeting, setGreeting] = useState("");
  const [mapSheet, setMapSheet] = useState(false);
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const path = location.pathname;
  const visible = path === "/" || path.startsWith("/esplora") || path.startsWith("/attivita") || path.startsWith("/app");

  useEffect(() => {
    api.get("/assistant/config").then(({ data }) => { setEnabled(!!data.enabled); setGreeting(data.greeting || ""); }).catch(() => {});
  }, []);

  // Evita sovrapposizioni con la X/controlli del comune: nascondi il FAB quando un pannello mappa è aperto.
  useEffect(() => {
    const h = (e) => setMapSheet(!!e.detail);
    window.addEventListener("fuma-map-sheet", h);
    return () => window.removeEventListener("fuma-map-sheet", h);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0 && greeting) setMessages([{ role: "assistant", text: greeting }]);
  }, [open, greeting]); // eslint-disable-line

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, loading]);

  // Meno invasivo: la bolla di benvenuto è breve e si nasconde dopo pochi secondi.
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => setShowBubble(false), 6000);
    return () => clearTimeout(t);
  }, [open]);

  if (!enabled || !visible || (mapSheet && !open)) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setShowBubble(false);
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, session_id: sessionId(), context: path });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, cards: data.cards || [], actions: data.actions || [], disclaimer: data.disclaimer }]);
      play("ai_notify");
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: formatApiError(e.response?.data?.detail) || "Assistente non disponibile al momento." }]);
    }
    setLoading(false);
  };

  const addToCart = (c) => {
    addItem({ id: c.id, name: c.name, price: c.price }, { id: c.business_id, name: c.business_name });
    toast.success(`${c.name} aggiunto al carrello`);
  };

  const doAction = async (a) => {
    if (a.type === "navigate") { setOpen(false); navigate(a.to); }
  };

  return (
    <>
      {!open && (
        <div className={`fixed z-[60] right-4 ${path.startsWith("/esplora") ? "bottom-[188px]" : "bottom-[88px]"} flex flex-col items-end gap-2`}>
          {showBubble && (
            <div data-testid="assistant-greeting-bubble" className="relative max-w-[220px] rounded-2xl rounded-br-sm bg-[#0F1016]/95 backdrop-blur-xl border border-[#00E676]/30 px-3.5 py-2.5 text-sm text-zinc-200 shadow-xl animate-[slideUp_0.3s_ease-out]">
              <button onClick={() => setShowBubble(false)} className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-[#1C1C1F] border border-white/15 flex items-center justify-center text-zinc-400"><X className="h-3 w-3" /></button>
              Ciao! Posso aiutarti?
            </div>
          )}
          <button data-testid="assistant-fab" onClick={() => { setOpen(true); setShowBubble(false); }}
            className="h-14 w-14 rounded-full bg-[#00E676] text-black flex items-center justify-center shadow-[0_8px_30px_rgba(0,230,118,0.45)] active:scale-95 transition-transform">
            <MessageCircle className="h-6 w-6" />
          </button>
        </div>
      )}

      {open && (
        <div data-testid="assistant-panel" className="fixed z-[60] right-3 bottom-3 w-[min(400px,calc(100vw-24px))] h-[min(620px,calc(100vh-24px))] flex flex-col rounded-3xl bg-[#0A0A0C] border border-white/12 shadow-[0_20px_60px_rgba(0,0,0,0.7)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#0F1016]">
            <div className="flex items-center gap-2"><div className="h-8 w-8 rounded-full bg-[#00E676]/15 border border-[#00E676]/40 flex items-center justify-center"><Bot className="h-4 w-4 text-[#00E676]" /></div><div><div className="font-semibold text-sm">Assistente Fuma</div><div className="text-[10px] text-zinc-500">Sempre qui per te</div></div></div>
            <button data-testid="assistant-close" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white"><X className="h-5 w-5" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar px-3 py-3 space-y-3">
            {messages.map((m, i) => (
              <div key={i}>
                <div data-testid={`assistant-msg-${m.role}`} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-[#00E676] text-black rounded-br-sm" : "bg-[#1C1C1F] border border-white/10 text-zinc-100 rounded-bl-sm"}`}>{m.text}</div>
                {(m.cards || []).length > 0 && (
                  <div className="mt-2 space-y-2">
                    {m.cards.map((c, ci) => c.kind === "business" ? (
                      <button key={ci} data-testid={`assistant-card-business-${c.id}`} onClick={() => { setOpen(false); navigate(`/attivita/${c.id}`); }} className="w-full text-left flex gap-3 rounded-xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                        <div className="h-12 w-12 shrink-0 rounded-lg overflow-hidden bg-[#1C1C1F] flex items-center justify-center border border-white/10">{c.image ? <img src={c.image} alt={c.name} className="h-full w-full object-cover" /> : <Store className="h-5 w-5 text-white/30" />}</div>
                        <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{c.name}{c.has_offer && <span className="ml-1 text-[9px] text-red-300 bg-red-500/15 border border-red-500/30 rounded px-1 py-0.5">Offerta</span>}</div><div className="text-[11px] text-zinc-500">{c.category} · {c.zone} · ~{c.prep_time} min</div></div>
                      </button>
                    ) : c.kind === "product" ? (
                      <div key={ci} data-testid={`assistant-card-product-${c.id}`} className="rounded-xl p-2.5 bg-[#131316] border border-white/10">
                        <div className="flex items-center justify-between gap-2"><div className="min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-[11px] text-zinc-500 truncate">{c.business_name}</div></div><div className="text-[#00E676] font-semibold text-sm whitespace-nowrap">€{Number(c.price).toFixed(2)}</div></div>
                        {c.description && <div className="text-[11px] text-zinc-500 mt-1">{c.description}</div>}
                        {(c.allergens || []).length > 0 && <div className="text-[10px] text-amber-300 mt-1">Allergeni: {c.allergens.join(", ")}</div>}
                        <button data-testid={`assistant-add-${c.id}`} onClick={() => addToCart(c)} className="mt-2 inline-flex items-center gap-1 text-xs bg-[#00E676] text-black rounded-full px-3 py-1.5 font-medium active:scale-95 transition-transform"><Plus className="h-3.5 w-3.5" /> Aggiungi al carrello</button>
                      </div>
                    ) : c.kind === "order" ? (
                      <button key={ci} data-testid={`assistant-card-order-${c.id}`} onClick={() => { setOpen(false); navigate(`/app/orders/${c.id}`); }} className="w-full text-left rounded-xl p-2.5 bg-[#131316] border border-white/10 hover:border-[#00E676]/40 transition-colors">
                        <div className="flex items-center gap-2"><ClipboardList className="h-4 w-4 text-[#00E676]" /><span className="font-semibold text-sm">Ordine #{String(c.id).slice(-5)}</span></div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">{c.business_name} · €{Number(c.total).toFixed(2)} · {String(c.status).replace(/_/g, " ")}</div>
                      </button>
                    ) : null)}
                  </div>
                )}
                {m.disclaimer && <div className="mt-1.5 text-[10px] text-amber-300/90 flex items-start gap-1"><Sparkles className="h-3 w-3 mt-0.5 shrink-0" /> {m.disclaimer}</div>}
                {(m.actions || []).map((a, ai) => a.type === "navigate" ? (
                  <button key={ai} data-testid={`assistant-action-${ai}`} onClick={() => doAction(a)} className="mt-2 inline-flex items-center gap-1 text-xs bg-white/8 border border-white/15 rounded-full px-3 py-1.5 hover:bg-white/12">{a.label}</button>
                ) : (
                  <div key={ai} className="mt-2 inline-flex items-center gap-1 text-xs text-[#00E676]"><LifeBuoy className="h-3.5 w-3.5" /> {a.label}</div>
                ))}
              </div>
            ))}
            {loading && <div className="bg-[#1C1C1F] border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-zinc-400 w-fit">Sto pensando…</div>}
            {messages.length <= 1 && !loading && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button data-testid="assistant-experiences-cta" onClick={() => { setOpen(false); navigate("/esperienze"); }} className="text-xs rounded-full bg-gradient-to-r from-[#7DF9FF]/20 to-[#00E676]/15 border border-[#7DF9FF]/40 text-[#7DF9FF] px-3 py-1.5 hover:from-[#7DF9FF]/30">✨ Prova le Esperienze AI</button>
                {SUGGESTIONS.map((s, i) => (
                  <button key={i} data-testid={`assistant-suggestion-${i}`} onClick={() => send(s)} className="text-xs rounded-full border border-[#00E676]/30 text-[#00E676] px-3 py-1.5 hover:bg-[#00E676]/10">{s}</button>
                ))}
              </div>
            )}
          </div>

          <div className="p-2.5 border-t border-white/10 bg-[#0F1016] flex items-center gap-2">
            <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Scrivi un messaggio…" className="flex-1 bg-[#0A0A0C] border border-white/10 rounded-full px-4 py-2.5 text-sm outline-none focus:border-[#00E676]/50" />
            <button data-testid="assistant-send" onClick={() => send()} disabled={loading || !input.trim()} className="h-10 w-10 rounded-full bg-[#00E676] text-black flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform"><Send className="h-4 w-4" /></button>
          </div>
        </div>
      )}
    </>
  );
}
